import { useEffect } from "react";
import { db } from "./indexedDB";
import API_DOMAIN from "../config/config";

// Generic Sync Function
const syncWithServer = async (config) => {
  const {
    storeName,
    apiEndpoint,
    keyName,
    companyIdKey = null,
    updateKey = "isUpdate",
  } = config;

  if (!navigator.onLine) return;

  const companyId = companyIdKey ? localStorage.getItem(companyIdKey) : null;
  if (companyIdKey && !companyId) {
    console.error(`❌ ${storeName} sync failed: Company ID not found!`);
    return;
  }

  const unsyncedData = await db[storeName].getUnsynced();
  console.log(`📤 Unsynced ${storeName}:`, unsyncedData);

  for (const item of unsyncedData) {
    try {
      console.log(`🚀 Syncing ${storeName}:`, item);
      const {
        [keyName]: id,
        [updateKey]: isUpdate,
        remark = "",
        ...data
      } = item;

      const method = isUpdate ? "PUT" : "POST";
      const url = `${API_DOMAIN}/${apiEndpoint}${
        companyId ? `?id=${companyId}` : ""
      }`;
      if (isUpdate) data[keyName] = id;
      if (companyId) data.companyId = companyId;

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!response.ok) throw new Error(`Server error: ${response.statusText}`);

      const responseData = await response.json();
      console.log(`🌍 ${storeName} Response:`, responseData);

      if (responseData.status === 200 || responseData.head?.code === 200) {
        console.log(`✅ ${storeName} synced successfully:`, id);
        await db[storeName].remove(id);
      } else {
        console.error(
          `❌ ${storeName} Error:`,
          responseData.msg || responseData.head?.msg
        );
      }
    } catch (error) {
      console.error(`❌ ${storeName} Sync failed:`, error);
    }
  }
};

// Sync Configurations
const syncConfigs = [
  { storeName: "users", apiEndpoint: "user.php", keyName: "user_id" },
  {
    storeName: "invoices",
    apiEndpoint: "wholesale_invoice.php",
    keyName: "invoice_id",
  },
  {
    storeName: "sales_invoices",
    apiEndpoint: "salesinvoice.php",
    keyName: "invoice_id",
    companyIdKey: "companyId",
  },
  {
    storeName: "purchase",
    apiEndpoint: "purchase_bill.php",
    keyName: "purchase_id",
    companyIdKey: "companyId",
  },
];

// Auto-sync Hook
export const useAutoSync = () => {
  useEffect(() => {
    const syncAll = () =>
      syncConfigs.forEach((config) => syncWithServer(config));
    window.addEventListener("online", syncAll);
    if (navigator.onLine) syncAll();
    return () => window.removeEventListener("online", syncAll);
  }, []);
};

// Individual Sync Functions (Optional)
export const syncUsersWithServer = () => syncWithServer(syncConfigs[0]);
export const syncInvoicesWithServer = () => syncWithServer(syncConfigs[1]);
export const syncSalesInvoicesWithServer = () => syncWithServer(syncConfigs[2]);
export const syncPurchaseWithServer = () => syncWithServer(syncConfigs[3]);
